import { DeviceMode, DeviceStyle, NodeV2, NodeV2Style, toArrayValue } from '@vedalib/editor'
import { generateId } from '@vedalib/editor/lib/utils/id'
import * as R from 'ramda'

import { t } from 'services/Translation'

import {
  DEFAULT_FULL_SIZE,
  FR_1,
  PX_12,
  PX_20,
  PX_60,
  PX_92,
  PX_98,
  PX_115,
  toArray,
  genNode,
} from '../constants'
import { saveGrid } from '../formControls/Grid/utils'
import { ISizeValue } from '../formControls/Size/types'
import { saveSize } from '../formControls/Size/utils'
import { savePxSizes } from '../formControls/Sizes/utils'
import columns2 from './assets/2columns.svg'
import rows2 from './assets/2rows.svg'
import columns3 from './assets/3columns.svg'
import rows3 from './assets/3rows.svg'
import grid940 from './assets/940px.svg'
import grid1280 from './assets/1280px.svg'
import grid1440 from './assets/1440px.svg'
import grid1600 from './assets/1600px.svg'

const DEFAULT_STYLE: DeviceStyle = {
  [DeviceMode.desktop]: {
    width: DEFAULT_FULL_SIZE,
    height: null,
    padding: null,
    gridColumns: saveGrid({
      min: 20,
      items: [FR_1, ...toArray(PX_60, 12), FR_1].map(toArrayValue),
    }),
    gridColumnsGap: 20,
    flexDirection: 'row',
  },
  [DeviceMode.tablet]: {
    gridColumnsGap: 8,
    gridColumns: saveGrid({
      min: 8,
      items: [PX_20, ...toArray(FR_1, 12), PX_20].map(toArrayValue),
    }),
  },
  [DeviceMode.mobile]: {
    gridColumnsGap: 4,
    gridColumns: saveGrid({
      min: 8,
      items: [PX_12, ...toArray(FR_1, 12), PX_12].map(toArrayValue),
    }),
  },
}

export interface IRootPreset {
  id: string
  title: string
  image: string
  style: DeviceStyle
  children?: NodeV2[]
}

export const PROP_KEYS: (keyof NodeV2Style)[] = [
  'display',
  'gridColumns',
  'gridColumnsGap',
  'flexDirection',
]

const genColumn = (width: ISizeValue) =>
  genNode(generateId(), {
    style: {
      [DeviceMode.desktop]: {
        width: saveSize(width),
        display: 'flex',
        flexDirection: 'column',
        minHeight: saveSize({ value: 100, unit: 'px' }),
        flexGrow: 1,
      },
    },
  })

const genRow = () =>
  genNode(generateId(), {
    style: {
      [DeviceMode.desktop]: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: saveSize({ value: 100, unit: 'px' }),
        flexGrow: 1,
      },
    },
  })

export const PRESETS: IRootPreset[] = [
  {
    id: 'grid-940',
    title: '940px',
    image: grid940,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: { display: 'grid' },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
  },
  {
    id: 'grid-1280',
    title: '1280px',
    image: grid1280,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: {
        display: 'grid',
        gridColumnsGap: 16,
        gridColumns: saveGrid({
          min: 20,
          items: [FR_1, ...toArray(PX_92, 12), FR_1].map(toArrayValue),
        }),
      },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
  },
  {
    id: 'grid-1440',
    title: '1440px',
    image: grid1440,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: {
        display: 'grid',
        gridColumnsGap: 24,
        gridColumns: saveGrid({
          min: 24,
          items: [FR_1, ...toArray(PX_98, 12), FR_1].map(toArrayValue),
        }),
      },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
  },
  {
    id: 'grid-1600',
    title: '1600px',
    image: grid1600,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: {
        display: 'grid',
        gridColumnsGap: 20,
        gridColumns: saveGrid({
          min: 24,
          items: [FR_1, ...toArray(PX_115, 12), FR_1].map(toArrayValue),
        }),
      },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
  },
  {
    id: '2-columns',
    title: t('editorV2.presets.root.2columns'),
    image: columns2,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: {
        display: 'flex',
        padding: savePxSizes([24, 24, 24, 24]),
        flexGap: 24,
      },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
    children: [genColumn({ value: 50, unit: '%' }), genColumn({ value: 50, unit: '%' })],
  },
  {
    id: '3-columns',
    title: t('editorV2.presets.root.3columns'),
    image: columns3,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: {
        display: 'flex',
        padding: savePxSizes([24, 24, 24, 24]),
        flexGap: 24,
      },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
    children: [
      genColumn({ value: 33, unit: '%' }),
      genColumn({ value: 33, unit: '%' }),
      genColumn({ value: 33, unit: '%' }),
    ],
  },
  {
    id: '2-rows',
    title: t('editorV2.presets.root.2rows'),
    image: rows2,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: {
        display: 'flex',
        flexDirection: 'column',
        padding: savePxSizes([24, 24, 24, 24]),
        flexGap: 24,
      },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
    children: [genRow(), genRow()],
  },
  {
    id: '3-rows',
    title: t('editorV2.presets.root.3rows'),
    image: rows3,
    style: R.mergeDeepRight<DeviceStyle, DeviceStyle>(DEFAULT_STYLE, {
      [DeviceMode.desktop]: {
        display: 'flex',
        width: DEFAULT_FULL_SIZE,
        height: null,
        flexDirection: 'column',
        padding: savePxSizes([24, 24, 24, 24]),
        flexGap: 24,
      },
      [DeviceMode.tablet]: {},
      [DeviceMode.mobile]: {},
    }),
    children: [genRow(), genRow(), genRow()],
  },
]
